import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Building2, FileText, Tag, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import StatusChip from '../components/StatusChip';
import USComplianceMap from '../components/USComplianceMap';
import { api } from '../utils/api';
import { formatDate, getDaysUntil } from '../utils/helpers';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [dueItems, setDueItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    
    // Refresh data when window/tab regains focus
    const handleFocus = () => {
      loadDashboardData();
    };
    
    window.addEventListener('focus', handleFocus);
    
    // Also refresh every 60 seconds to keep stats current
    const intervalId = setInterval(loadDashboardData, 60000);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(intervalId);
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsData, dueData] = await Promise.all([
        api.getDashboardStats(),
        api.getDueItems('30')
      ]);
      setStats(statsData);
      setDueItems(dueData);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStateClick = (stateCode) => {
    // Navigate to State Matrix with the selected state
    navigate(`/states?state=${stateCode}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-container">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">Compliance Overview</h1>
        <p className="text-muted-foreground mt-2">Welcome back, {user.full_name}</p>
      </div>

      {/* Compliance KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Licensed States */}
        <Card 
          className="card-hover cursor-pointer transition-all hover:shadow-lg" 
          data-testid="kpi-card-licensed-states"
          onClick={() => navigate('/states')}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm sm:text-base font-medium text-muted-foreground">States with Licenses</CardTitle>
              <Building2 className="h-5 w-5 text-emerald-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl sm:text-4xl font-bold tracking-tight text-emerald-600">{stats?.licensed_states || 0}</div>
            <p className="text-sm text-muted-foreground mt-2">
              {stats?.total_licenses ? `${stats.total_licenses} total licenses` : 'States holding active licenses'}
            </p>
          </CardContent>
        </Card>

        {/* Renewals Due in 60 Days */}
        <Card 
          className="card-hover cursor-pointer transition-all hover:shadow-lg" 
          data-testid="kpi-card-renewals-60"
          onClick={() => {
            const element = document.querySelector('[data-testid="due-items-card"]');
            if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm sm:text-base font-medium text-muted-foreground">Renewals Due in 60 Days</CardTitle>
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl sm:text-4xl font-bold tracking-tight text-amber-600">{stats?.renewals_due_60 || 0}</div>
            <p className="text-sm text-muted-foreground mt-2">Upcoming deadlines</p>
          </CardContent>
        </Card>

        {/* Critical Issues */}
        <Card 
          className="card-hover cursor-pointer transition-all hover:shadow-lg" 
          data-testid="kpi-card-critical-issues"
          onClick={() => navigate('/licenses')}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm sm:text-base font-medium text-muted-foreground">Critical Issues</CardTitle>
              <AlertTriangle className="h-5 w-5 text-rose-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl sm:text-4xl font-bold tracking-tight text-rose-600">{stats?.critical_issues || 0}</div>
            <p className="text-sm text-muted-foreground mt-2">Requires immediate action</p>
          </CardContent>
        </Card>

        {/* States Requiring Attention */}
        <Card 
          className="card-hover cursor-pointer transition-all hover:shadow-lg" 
          data-testid="kpi-card-states-attention"
          onClick={() => {
            const element = document.querySelector('[data-testid="us-compliance-map"]');
            if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm sm:text-base font-medium text-muted-foreground">States Requiring Attention</CardTitle>
              <FileText className="h-5 w-5 text-amber-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl sm:text-4xl font-bold tracking-tight text-amber-500">{stats?.states_requiring_attention || 0}</div>
            <p className="text-sm text-muted-foreground mt-2">Yellow or red status</p>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Heat Map */}
      <USComplianceMap onStateClick={handleStateClick} />

      {/* Due Items Section */}
      <div className="grid grid-cols-1 gap-6">
        {/* Upcoming Renewals */}
        <Card data-testid="due-items-card">
          <CardHeader>
            <CardTitle>Upcoming Renewals</CardTitle>
            <CardDescription>All items due in the next 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Licenses */}
              {dueItems?.licenses?.map((license) => {
                const daysUntil = getDaysUntil(license.expiration_date);
                return (
                  <div key={`license-${license.license_id}`} className="flex items-start justify-between p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors" data-testid={`license-item-${license.license_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium text-muted-foreground">License</span>
                      </div>
                      <div className="font-medium text-sm sm:text-base">{license.license_type}</div>
                      <div className="text-xs sm:text-sm text-muted-foreground mt-1.5">{license.state} • {license.license_number}</div>
                      <div className="text-xs sm:text-sm text-muted-foreground mt-1">Expires: {formatDate(license.expiration_date)}</div>
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <Badge variant={daysUntil <= 7 ? 'destructive' : daysUntil <= 30 ? 'default' : 'secondary'} className="text-xs sm:text-sm">
                        {daysUntil} days
                      </Badge>
                    </div>
                  </div>
                );
              })}

              {/* DBAs */}
              {dueItems?.dbas?.map((dba) => {
                const daysUntil = getDaysUntil(dba.expiration_date);
                return (
                  <div key={`dba-${dba.dba_id}`} className="flex items-start justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors" data-testid={`dba-item-${dba.dba_id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Tag className="h-3 w-3 text-purple-500" />
                        <span className="text-xs font-medium text-muted-foreground">DBA</span>
                      </div>
                      <div className="font-medium text-sm">{dba.dba_name}</div>
                      <div className="text-xs text-muted-foreground mt-1">{dba.state} {dba.county ? `• ${dba.county} County` : ''}</div>
                      <div className="text-xs text-muted-foreground mt-1">Expires: {formatDate(dba.expiration_date)}</div>
                    </div>
                    <div className="text-right">
                      <Badge variant={daysUntil <= 7 ? 'destructive' : daysUntil <= 30 ? 'default' : 'secondary'}>
                        {daysUntil} days
                      </Badge>
                    </div>
                  </div>
                );
              })}

              {/* Filings */}
              {dueItems?.filings?.map((filing) => {
                const daysUntil = getDaysUntil(filing.due_date);
                return (
                  <div key={`filing-${filing.filing_id}`} className="flex items-start justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors" data-testid={`filing-item-${filing.filing_id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="h-3 w-3 text-green-500" />
                        <span className="text-xs font-medium text-muted-foreground">Filing</span>
                      </div>
                      <div className="font-medium text-sm">{filing.filing_type}</div>
                      <div className="text-xs text-muted-foreground mt-1">{filing.state}</div>
                      <div className="text-xs text-muted-foreground mt-1">Due: {formatDate(filing.due_date)}</div>
                    </div>
                    <div className="text-right">
                      <Badge variant={daysUntil <= 7 ? 'destructive' : daysUntil <= 30 ? 'default' : 'secondary'}>
                        {daysUntil} days
                      </Badge>
                    </div>
                  </div>
                );
              })}

              {/* State Requirements */}
              {dueItems?.requirements?.map((req) => {
                const daysUntil = getDaysUntil(req.next_renewal_date);
                return (
                  <div key={`requirement-${req.req_id}`} className="flex items-start justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors" data-testid={`requirement-item-${req.req_id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="h-3 w-3 text-orange-500" />
                        <span className="text-xs font-medium text-muted-foreground">State Requirement</span>
                      </div>
                      <div className="font-medium text-sm">{req.compliance_type}</div>
                      <div className="text-xs text-muted-foreground mt-1">{req.state}</div>
                      <div className="text-xs text-muted-foreground mt-1">Due: {formatDate(req.next_renewal_date)}</div>
                    </div>
                    <div className="text-right">
                      <Badge variant={daysUntil <= 7 ? 'destructive' : daysUntil <= 30 ? 'default' : 'secondary'}>
                        {daysUntil} days
                      </Badge>
                    </div>
                  </div>
                );
              })}

              {/* Empty State */}
              {(!dueItems?.licenses?.length && !dueItems?.dbas?.length && !dueItems?.filings?.length && !dueItems?.requirements?.length) && (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No renewals due in the next 30 days</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
